/**
 * Markdown for assistant replies.
 *
 * escapeHtml first, exactly as the share page does, so the model cannot emit
 * raw HTML into the app. Blocks are memoised so a token arriving only
 * re-renders the trailing block, which keeps a selection the user made
 * earlier from being torn out from under them mid stream.
 */
import { memo, useEffect, useMemo, useState } from "react";
import s from "./chat.module.css";

function escapeHtml(md: string): string {
  return md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Split on blank lines, but never inside a fenced code block. */
function splitBlocks(source: string): string[] {
  const out: string[] = [];
  let current: string[] = [];
  let inFence = false;
  for (const line of source.split("\n")) {
    if (/^\s*```/.test(line)) inFence = !inFence;
    if (!inFence && line.trim() === "") {
      if (current.length) out.push(current.join("\n"));
      current = [];
      continue;
    }
    current.push(line);
  }
  if (current.length) out.push(current.join("\n"));
  return out;
}

type Parse = (md: string) => string;

let parser: Parse | null = null;
let loading: Promise<Parse> | null = null;

function loadParser(): Promise<Parse> {
  if (parser) return Promise.resolve(parser);
  loading ??= import("marked").then(({ marked }) => {
    parser = (md: string) => marked.parse(escapeHtml(md), { async: false, gfm: true }) as string;
    return parser;
  });
  return loading;
}

const Block = memo(function Block({ html }: { html: string }) {
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
});

export function Markdown({ source }: { source: string }) {
  const [ready, setReady] = useState(!!parser);
  useEffect(() => {
    if (ready) return;
    let alive = true;
    void loadParser().then(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, [ready]);

  const blocks = useMemo(() => splitBlocks(source), [source]);
  if (!ready || !parser) return <div className={s.md}>{source}</div>;
  const parse = parser;
  return (
    <div
      className={s.md}
      onClick={(e) => {
        const pre = (e.target as HTMLElement).closest("pre");
        if (!pre) return;
        void navigator.clipboard.writeText(pre.textContent ?? "");
      }}
    >
      {blocks.map((b, i) => (
        <Block key={i} html={parse(b)} />
      ))}
    </div>
  );
}
